import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Label,
    LabeledContainer,
    LabeledContainerBody,
    LabeledContainerHeader,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    type ListRenderItemInfo,
    ListSkeleton,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { UserAvatar } from '../Users';

type ChangeRequestParticipantsListProps = {
    changeRequest: api.ChangeRequest;
};

/**
 * List the participants for a change request.
 */
export function ChangeRequestParticipantsList(props: ChangeRequestParticipantsListProps) {
    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <Label>Participants</Label>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <React.Suspense fallback={<ListSkeleton compact />}>
                    <ChangeRequestParticipantsListBody {...props} />
                </React.Suspense>
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

function ChangeRequestParticipantsListBody(props: { changeRequest: api.ChangeRequest }) {
    const { changeRequest } = props;

    const { data: edits } = useSuspenseQuery(
        apiQuery('getContributorsByChangeRequestId', {
            spaceId: changeRequest.space,
            changeRequestId: changeRequest.id,
        })
    );

    return (
        <List
            items={edits.items.sort((a, b) => a.count - b.count)}
            renderItem={({ item }: ListRenderItemInfo<api.ChangeRequestUserContributor>) => (
                <ListItem compact>
                    <ListItemIndicator>
                        <UserAvatar size="md" tooltip={false} user={item.user} className="mr-1" />
                    </ListItemIndicator>
                    <ListItemBody>
                        <ListItemDescription>{item.user.displayName}</ListItemDescription>
                        <ListItemDetail>
                            {ACTION_TYPE_LABELS[item.actionType]}{' '}
                            <DateShortRelative value={item.updatedAt} onlyDate={false} />
                        </ListItemDetail>
                    </ListItemBody>
                    {item.actionType === 'comment' ? (
                        <ListItemControls when="always">
                            <Button
                                kind="blank"
                                leadingIcon={Icon.Comment}
                                label="View comments"
                                iconOnly
                                href={{
                                    screen: {
                                        sideSheet: {
                                            type: 'comments',
                                        },
                                    },
                                }}
                            />
                        </ListItemControls>
                    ) : null}
                </ListItem>
            )}
        />
    );
}

const ACTION_TYPE_LABELS: Record<api.ChangeRequestUserContributor['actionType'], string> = {
    comment: 'Commented',
    edit: 'Edited',
};
